import { useState, useRef, useEffect } from "react"
import { useQueryClient } from "react-query"
import { ContentItem, ContentItemContent, ContentItemTitle } from "./styled"
import { v4 as uuidv4 } from "uuid"
import { useAxios } from "../../../../axiosProvider"
import { RotatingLines } from "react-loader-spinner"
import NavButtonWithCustomMenu from "./NavButtonWithCustomMenu"
import styled from "styled-components"
import Button from "@mui/material/Button"
import downloadFilesAsZip from "./downloadFolderFromFirebase"
import {
  useFetchEntities,
  useDeleteTourGroupFile,
  useDeleteAllTourGroupFiles,
} from "../../../../reactQueryHooks"
import moment from "moment"
import io from "socket.io-client"

const socket = io("https://getaways-bookings.herokuapp.com", {
  transports: ["websocket"],
})

//-------------------------------------------

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 5px;
  width: 100%;
  height: 100%;
  overflow: hidden;
`

const FilesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 0 2px 0 0;
  max-height: 200px;
`

const FileContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 5px;
  border-radius: 3px;
  background-color: #ebebeb;
  cursor: pointer;

  font-size: 11px;
  .filename {
    &:hover {
      color: dodgerblue;

      & + .icons-container .fa-download {
        color: dodgerblue;
      }
    }
  }
  .fa-trash-alt {
    &:hover {
      color: indianred;
    }
  }
`

const IconsContainer = styled.div`
  display: flex;
  gap: 10px;
  i {
    font-size: 10px;
    cursor: pointer;
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

//-------------------------------------------

const SortableTableInfoFiles = ({ tourGroup }) => {
  const [instanceId] = useState(uuidv4())
  const queryClient = useQueryClient()
  const { mutateAsync: deleteTourGroupFile } = useDeleteTourGroupFile()
  const { mutateAsync: deleteAllTourGroupFiles } = useDeleteAllTourGroupFiles()

  const { data: entities } = useFetchEntities()
  const product = entities?.data?.products?.find(
    (product) => product._id === tourGroup.product_id
  )
  const date = moment(tourGroup.date).format("DD-MM-YYYY")
  const fileName = `${product?.title} ${date} ${tourGroup.time} #${tourGroup.index}`
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const [isDownloadingFiles, setIsDownloadingFiles] = useState(false)
  const [isDeletingAllFiles, setIsDeletingAllFiles] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef(null)

  const { user, axiosInstance } = useAxios()
  const userIsAdmin = user.isAdmin
  const userIsPermittedToUploadFiles =
    user.permissions.uploadFiles || userIsAdmin

  const folderFilesUrls = tourGroup.tour_group_files || []

  //-------------------------------------------

  const handleFilesUpload = async (files) => {
    if (!files || files.length === 0) return
    try {
      setIsUploadingFile(true)
      setUploadProgress(0)
      const formData = new FormData()
      for (let i = 0; i < files.length; i++) {
        formData.append("files", files[i])
      }
      formData.append("instance_id", instanceId)
      await axiosInstance.post(
        `api/tour_groups/${tourGroup._id}/files`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (pe) => {
            if (pe.total) {
              setUploadProgress(Math.round((pe.loaded / pe.total) * 100))
            }
          },
        }
      )
      queryClient.invalidateQueries(["TOUR_GROUP", tourGroup._id])
    } catch (error) {
      console.error("Error uploading files:", error)
    } finally {
      setIsUploadingFile(false)
      setUploadProgress(0)
    }
  }

  const handleDeleteFile = async (filename) => {
    try {
      await deleteTourGroupFile({
        tourGroupId: tourGroup._id,
        filename,
        instance_id: instanceId,
      })
    } catch (error) {
      console.error("Error deleting file:", error)
      alert("Error deleting file")
    }
  }

  const handleDeleteAllFiles = async () => {
    try {
      await deleteAllTourGroupFiles({
        tourGroupId: tourGroup._id,
        instance_id: instanceId,
      })
    } catch (error) {
      console.error("Error deleting files:", error)
      alert("Error deleting files")
    }
  }

  const noFolderOrFiles = !folderFilesUrls || folderFilesUrls.length === 0

  useEffect(() => {
    const handleRefreshFiles = (data) => {
      if (data.instance_id !== instanceId) {
        queryClient.invalidateQueries(["TOUR_GROUP", tourGroup._id])
      }
    }

    socket.on(
      `getaways_suite/refresh_files/${tourGroup._id}`,
      handleRefreshFiles
    )

    return () => {
      socket.off(
        `getaways_suite/refresh_files/${tourGroup._id}`,
        handleRefreshFiles
      )
    }
  }, [queryClient, tourGroup._id, instanceId])

  //-------------------------------------------
  return (
    <NavButtonWithCustomMenu
      title={
        <ContentItem
          title="Upload"
          initial={{ opacity: 0, x: -5 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -5 }}
          transition={{ duration: 0.25, delay: 0.25 }}
        >
          <ContentItemTitle>Files</ContentItemTitle>
          <ContentItemContent>
            <i className="fa-solid fa-file"></i>
            <span>See files</span>
            <span
              style={{
                color: noFolderOrFiles ? "indianred" : "darkgreen",
              }}
            >
              {`(${folderFilesUrls?.length})`}
            </span>
            {isUploadingFile ? (
              <span
                style={{
                  fontWeight: "bold",
                  fontSize: "10px",
                }}
              >
                {uploadProgress.toFixed(0)}% Uploading...
              </span>
            ) : (
              <input
                ref={fileInputRef}
                style={{
                  display: "none",
                }}
                type="file"
                accept="*"
                multiple
                onChange={(e) => {
                  handleFilesUpload(e.target.files)
                }}
              />
            )}
          </ContentItemContent>
        </ContentItem>
      }
    >
      <Wrapper>
        {!noFolderOrFiles && (
          <FilesContainer>
            {folderFilesUrls.map((file) => {
              return (
                <FileContainer key={file.filename}>
                  <span
                    title="Download file"
                    onClick={(event) => {
                      event.stopPropagation()
                      window.open(file.url, "_blank")
                    }}
                    className="filename"
                  >
                    {file.filename}
                  </span>
                  <IconsContainer className="icons-container">
                    <i className="fas fa-download"></i>
                    <i
                      title="Delete file"
                      onClick={() => {
                        if (userIsPermittedToUploadFiles) {
                          if (window.confirm("Delete this file?")) {
                            handleDeleteFile(file.filename)
                          }
                        } else {
                          alert("You do not have permission to delete files")
                        }
                      }}
                      className="fas fa-trash-alt"
                    ></i>
                  </IconsContainer>
                </FileContainer>
              )
            })}
          </FilesContainer>
        )}
        <ButtonsContainer>
          <Button
            variant="contained"
            style={{
              backgroundColor: "cornflowerblue",
              color: "white",
              fontSize: "10px",
              fontWeight: "bold",
            }}
            onClick={() => {
              if (userIsPermittedToUploadFiles) {
                fileInputRef.current.click()
              } else {
                alert("You do not have permission to upload files")
              }
            }}
          >
            UPLOAD FILES
          </Button>
          {!noFolderOrFiles && (
            <Button
              variant="contained"
              style={{
                backgroundColor: "grey",
                color: "white",
                fontSize: "10px",
                fontWeight: "bold",
              }}
              onClick={async (event) => {
                event.stopPropagation()
                try {
                  if (userIsPermittedToUploadFiles) {
                    setIsDownloadingFiles(true)
                    await downloadFilesAsZip(tourGroup._id, fileName)
                  } else {
                    alert("You do not have permission to download files")
                  }
                } catch (e) {
                  console.log(e)
                  alert("Error downloading files")
                } finally {
                  setIsDownloadingFiles(false)
                }
              }}
            >
              {isDownloadingFiles ? (
                <RotatingLines
                  width="17.5"
                  strokeColor="white"
                  strokeWidth={4}
                />
              ) : (
                "DOWNLOAD ALL FILES"
              )}
            </Button>
          )}
          {!noFolderOrFiles && (
            <Button
              variant="contained"
              style={{
                backgroundColor: "rgb(231 140 140)",
                color: "white",
                fontSize: "10px",
                fontWeight: "bold",
              }}
              onClick={async (event) => {
                event.stopPropagation()
                if (!userIsPermittedToUploadFiles) {
                  alert("You do not have permission to delete files")
                  return
                }
                if (window.confirm("Delete all files?")) {
                  try {
                    setIsDeletingAllFiles(true)
                    await handleDeleteAllFiles()
                  } catch (e) {
                    console.log(e)
                    alert("Error deleting files")
                  } finally {
                    setIsDeletingAllFiles(false)
                  }
                }
              }}
            >
              {isDeletingAllFiles ? (
                <RotatingLines
                  width="13"
                  strokeColor="white"
                  strokeWidth={4}
                />
              ) : (
                "DELETE ALL FILES"
              )}
            </Button>
          )}
        </ButtonsContainer>
      </Wrapper>
    </NavButtonWithCustomMenu>
  )
}

export default SortableTableInfoFiles
